import { type ComponentType, lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { SkeletonBodyText, SkeletonPage } from '@shopify/polaris';

import { AppLayout } from '@/components/AppLayout';

import { RedirectToSchedule } from './RedirectToSchedule';

/**
 * One chunk per page (see `vite.config.ts` manualChunks), and ONE map that
 * names them.
 *
 * ⚠ This object is the single source for both halves — the `lazy()` calls below
 * AND the idle prefetch work-list. Two lists would agree on the day they were
 * written and drift on the day a page is added, and the drift is invisible: the
 * page still works, it is simply the one page that is never warmed, and nothing
 * anywhere reports that.
 */
const pageLoaders = {
  home: () => import('@/pages/Home'),
  effects: () => import('@/pages/Effects'),
  decorations: () => import('@/pages/Decorations'),
  addToCart: () => import('@/pages/AddToCart'),
  cursor: () => import('@/pages/Cursor'),
  browserTab: () => import('@/pages/BrowserTab'),
  music: () => import('@/pages/Music'),
  schedule: () => import('@/pages/Schedule'),
  campaignEditor: () => import('@/pages/CampaignEditor'),
  settings: () => import('@/pages/Settings'),
  templates: () => import('@/pages/Templates'),
  plans: () => import('@/pages/Plans'),
} satisfies Record<string, () => Promise<{ default: ComponentType }>>;

// Lazy so the eager payload is Polaris + the shell, not every editor.
const Home = lazy(pageLoaders.home);
const Effects = lazy(pageLoaders.effects);
const Decorations = lazy(pageLoaders.decorations);
const AddToCart = lazy(pageLoaders.addToCart);
const Cursor = lazy(pageLoaders.cursor);
const BrowserTab = lazy(pageLoaders.browserTab);
const Music = lazy(pageLoaders.music);
const Schedule = lazy(pageLoaders.schedule);
const CampaignEditor = lazy(pageLoaders.campaignEditor);
const Settings = lazy(pageLoaders.settings);
const Templates = lazy(pageLoaders.templates);
const Plans = lazy(pageLoaders.plans);

/**
 * Warm the other page chunks once the first page has settled.
 *
 * `requestIdleCallback` fires only when the main thread is quiet — i.e. after
 * the first page has painted and its own network work is done — so this never
 * competes with the first paint. One chunk per idle slice, yielding between
 * each, so a slow connection's burst of requests cannot delay an interaction.
 *
 * Every loader is idempotent: the module registry and `React.lazy` both dedupe,
 * so this populates the exact cache the lazy component reads on navigation
 * rather than a cache of its own. Failures are swallowed on purpose — a warm
 * that did not happen costs a spinner later, and a rejected promise nobody is
 * awaiting is an unhandled rejection in the merchant's console for a request
 * that was never needed yet.
 *
 * ⚠ The `timeout` is not optional. Without it an admin that never goes idle —
 * a preview canvas animating on Home is exactly that — would never prefetch at
 * all, which is the case this was built for.
 */
let prefetchScheduled = false;
function prefetchPagesOnIdle() {
  if (prefetchScheduled || typeof window === 'undefined') return;
  prefetchScheduled = true;

  const scheduleIdle = (cb: () => void) => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => cb(), { timeout: 2000 });
    } else {
      // Safari had no `requestIdleCallback` until 2022 and Shopify's admin is
      // full of iPads. A timeout is a worse scheduler, not a missing feature.
      window.setTimeout(cb, 200);
    }
  };

  const loaders = Object.values(pageLoaders);
  const prefetchNext = (i: number) => {
    if (i >= loaders.length) return;
    scheduleIdle(() => {
      void loaders[i]().catch(() => {});
      prefetchNext(i + 1);
    });
  };
  prefetchNext(0);
}

/**
 * The shell, plus the one call that starts the prefetch.
 *
 * ⚠ The effect lives HERE and not inside `AppLayout`, and the reason is the
 * import graph rather than taste. This file imports `AppLayout`; for `AppLayout`
 * to call `prefetchPagesOnIdle` it would have to import it back out of
 * `@/routes`, and the two modules become a cycle. `vite.config.ts` carries a
 * long note on what a cycle costs in this app — chunks whose execution order
 * stops being derivable, and a module reading a binding that has not
 * initialised, which presents as a production-only blank screen that every
 * dev-server check passes. Moving the map into a third module would break the
 * cycle and the "one list" rule with it, since the `lazy()` calls have to sit
 * beside the routes they serve.
 *
 * WHERE it runs matters as much as that it runs. `AuthProvider` mounts before
 * the session resolves, so a prefetch started there would put twelve chunk
 * requests alongside `/verify` and the config load — `requestIdleCallback`
 * waits for a quiet main thread, not a quiet network, and the network is the
 * contended resource during boot. This component is the root route's element:
 * it mounts once, after `AuthProvider` has cleared its gate, with the first
 * page already rendering.
 *
 * ⚠ The lint rule's advice — move the component to its own file — is the third
 * module this note just rejected, and the warning it is protecting is already
 * moot: a module that exports `router` (a call expression, not a constant) can
 * never fast-refresh anyway, so there is no refresh here to preserve.
 */
// eslint-disable-next-line react-refresh/only-export-components
const AppShell = () => {
  useEffect(prefetchPagesOnIdle, []);
  return <AppLayout />;
};

/**
 * What a page looks like while its chunk is still arriving.
 *
 * ⚠ NOT `null`, which is what this was. A null fallback is invisible when the
 * chunk is already warm and a BLANK ADMIN when it is not — a merchant on a slow
 * connection clicks a nav item and gets an empty white frame with no indication
 * that anything is happening. Prefetching makes that rarer, not impossible: a
 * deep link straight into an editor, a reload on `/schedule`, or a click made
 * before the idle queue got that far all still land on a cold chunk.
 *
 * A skeleton rather than a spinner, and the same one `AuthProvider` boots with,
 * so the two loading states of an admin do not look like two different apps.
 * It also carries no text, which is the other reason to prefer it — a fallback
 * is not a place to need a translation.
 */
const PAGE_FALLBACK = (
  <SkeletonPage primaryAction>
    <SkeletonBodyText lines={6} />
  </SkeletonPage>
);

const wrap = (el: React.ReactNode) => <Suspense fallback={PAGE_FALLBACK}>{el}</Suspense>;

/**
 * The admin's routes.
 *
 * ── The shape, and why it is this one ──────────────────────────────────────
 *
 * Six surface pages hang off Home, one per thing the storefront can paint, and
 * each is a grid of toggle rows. `/schedule` is the old campaigns list with the
 * timeline promoted, and it is a nav item because it is the page the category
 * leader cannot have — nothing in their model knows when anything runs.
 *
 * ⚠ The word "campaign" survives HERE and only here: in the route of the deep
 * editor's component file and in the type names. On screen it appears in no
 * language.
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: wrap(<Home />) },

      { path: 'effects', element: wrap(<Effects />) },
      { path: 'decorations', element: wrap(<Decorations />) },
      { path: 'add-to-cart', element: wrap(<AddToCart />) },
      { path: 'cursor', element: wrap(<Cursor />) },
      { path: 'browser-tab', element: wrap(<BrowserTab />) },
      { path: 'music', element: wrap(<Music />) },

      { path: 'schedule', element: wrap(<Schedule />) },
      { path: 'schedule/:id', element: wrap(<CampaignEditor />) },

      // The old paths. A merchant's bookmark must not land in the SPA fallback,
      // which answers 200 with the shell and therefore looks like a blank app.
      { path: 'campaigns', element: <RedirectToSchedule /> },
      { path: 'campaigns/:id', element: <RedirectToSchedule /> },

      { path: 'templates', element: wrap(<Templates />) },
      { path: 'plans', element: wrap(<Plans />) },
      { path: 'settings', element: wrap(<Settings />) },
    ],
  },
]);
